/**
 * The neutral schema contract: every table of spec section 8 (with the additions decided
 * on 2026-09-28) described once with portable column types, then emitted twice: as
 * drizzle `pgTable` definitions (./pg, the Supabase side) and as `sqliteTable`
 * definitions (./sqlite, the device side). This module imports nothing from drizzle, so
 * it loads anywhere: web worker, API, tests and the two drizzle-kit configs.
 *
 * Naming: tables and columns use the Church's Indonesian terms exactly as the spec writes
 * them; code and comments are English (spec 8.1). Enumerations are short lowercase codes
 * stored as text; the Zod validators (../validators) enforce them.
 *
 * Deliberately absent: foreign keys and unique constraints on synced tables. Ops are
 * applied field by field and may arrive in any order across pull pages, so referential
 * and uniqueness rules are checked by the app and flagged in masalah_sinkron, never
 * enforced by the database (spec 9.3, 12.2). Search and FTS indexes belong to section 02.
 */

/**
 * Bumped on every schema change and carried by every op (spec 9.2, 9.10). Changes are
 * additive only from here on: never rename or drop a table or column ("stop writing,
 * keep reading"), and every new column must be nullable or carry a default so a creation
 * op from a device up to six months behind still inserts. The device stores this value
 * in `PRAGMA user_version` after migrating, so it also moves when a device-only
 * migration lands (2: the FTS5 search tables of section 02; no server change; 3: the
 * `ops.sebelum_*` columns of section 03, server only; 4: `masalah_sinkron.op` of
 * section 16, device only; 5: `mutasi.ringkasan` of section 12, both sides; 6: the
 * family's kabupaten/kota, provinsi, kode pos and KK issue date, the person's birth
 * certificate number (Dokumen jadi teks, 2026-09-30), both sides; the server table
 * baca_harian; 7: the `pelayan` and `penugasan` tables, `pengguna.stasi_id` and the
 * device's `sesi_lokal.stasi_id` of Pelayan & pengurus (2026-09-30), both sides; 8:
 * `tidak_tercatat` on the five registers, the required fields an old book does not record
 * (owner, 2026-10-02), both sides; and `pengaturan_paroki.kop_surat` takes five lines (the
 * official kop, 2026-10-02): an op below 8 comes from an app that reads three, so the push
 * refuses it a kop over a longer one, `KOP_LIMA_BARIS_SEJAK`; 9: the `kelompok` table and
 * `penugasan.kelompok_id` of the parish hierarchy (2026-10-02), both sides; the same release
 * drops the unit kind `wilayah`, the assignment level `wilayah` and `lainnya_wilayah` from the
 * lists (no column changes), and the pull holds back `kelompok` from an app that does not
 * name its version, `TABEL_SEJAK`); 10: the person's own address on `umat` (`alamat`, `rt_rw`,
 * `kampung_desa`, `kecamatan`, `kabupaten_kota`, `provinsi`), used while a person has no family
 * (upacara bersama, 2026-10-02), both sides; 11: the parish's logo, `pengaturan_paroki.logo`
 * (logo paroki, 2026-10-03), the town its letters are made in,
 * `pengaturan_paroki.tempat_surat`, the photo of a priest or religious, `pelayan.foto`
 * (owner, 2026-10-03), and an assignment's consent to appear on the parish website,
 * `penugasan.tampil_di_situs`, `tampil_di_situs_pada` and `tampil_di_situs_oleh` (umpan situs,
 * 2026-10-03), both sides; 12: the lingkungan the destination places an accepted family or
 * person in, `mutasi.lingkungan_tujuan_id` (owner, 2026-10-04: Terima asks for it), and the KK
 * number an accepted family leaves behind in its source parish, `mutasi.nomor_kk_asal` (owner,
 * 2026-10-04: a family that moves gets a new number there; the old one is retired), both sides.
 * The same release retires `imam` from sync (owner, 2026-10-04, step 1 of 2; `TABEL_PENSIUN`):
 * Postgres keeps the table, server only, and the device drops its local copy in a migration of
 * its own (`0014_pensiun_imam`) that needs no version: the migration journal tracks it. Release
 * 14 adds the server table `jatah_ganda` (the duplicate check's bounds) and stays at 12. 13: the
 * person's later sacraments typed on a baptism entry (`baptis.komuni_*`, `krisma_*`,
 * `perkawinan_*`; owner, 2026-10-08), and Pindah ke keuskupan lain on a family, a person and a
 * Pelayan (`pindah_keuskupan`, `pindah_paroki`, `pindah_tanggal`), both sides
 * (`0016_sakramen_lanjut_baptis`, `0017_pindah_keuskupan`). 14: a family's Kode keluarga,
 * `keluarga.kode_keluarga` (release 18, owner 2026-10-08: the government KK number is the
 * family's number everywhere, the parish's own code beside it), both sides
 * (`0018_kode_keluarga`); the device's search index takes it in `0019_cari_kode_keluarga`. 15:
 * the parish data sheet's fields (release 19, owner 2026-10-08): on `umat` the religion and the
 * previous one, the church of origin, the birth order, the school now attended, the field of
 * study, the school, the graduation year, the profession and the credit-union membership; on
 * `keluarga` the housing status; and the new synced table `umat_profil`, a person's sensitive
 * fields (blood type, ethnicity, income band, health insurance), which only parish staff and
 * admins receive (`TABEL_KHUSUS_STAF`), both sides (`0020_data_umat`). 16: Peta umat (release
 * 20, owner 2026-10-08): a family's home location on `keluarga` (`lat`, `lng`, `lokasi_sumber`,
 * `lokasi_presisi`, `lokasi_akurasi_m`, `lokasi_diubah_pada`, and the key of the address it
 * answers, `lokasi_alamat_kunci`, review I1) and a lingkungan's or a stasi's
 * centre point on `unit_teritorial` (`lat`, `lng`, `lokasi_sumber`), both sides
 * (`0021_peta_umat`); the server tables `geokode_cache` and `geokode_gerbang` of the address
 * lookup. Release 21 drops the retired `imam` table from Postgres (step 2 of 2, owner
 * 2026-10-04: "export and drop"; it held no row, and `20261012015343_hapus_imam` refuses to run
 * while it holds one) and stays at 16: no device column changes; `TABEL_PENSIUN` keeps the name.
 * Release 27 removes photo reading (owner, 2026-10-31) and drops its server table `baca_harian`
 * (`20261030154309_hapus_baca_harian`, which refuses to run while it holds a row) and stays at 16: the
 * table was never synced, and the device keeps its `dokumen_lokal` table, emptied, so no device
 * migration is added. 17: Hapus umat, Hapus keluarga and Hapus akun (release 31, owner 2026-11-02):
 * the reason a person, a family or an account was deleted, `alasan_hapus` on `umat`, `keluarga`
 * and `pengguna`, both sides (`0022_alasan_hapus`). Release 32 adds the role `pastor_paroki` (owner
 * 2026-11-02) and stays at 17: no column changes, `pengguna.peran` has no check in either dialect,
 * and an app on 17 stores and pulls such a row like any other.
 */
export const SCHEMA_VERSION = 17

/**
 * Column types allowed on synced tables. Each maps to one SQLite storage class and one
 * Postgres type without conversion: text stays text, integers stay integers (SQLite
 * integers are 64-bit, so `bigint` is portable too), booleans are `boolean` in Postgres
 * and `integer` 0/1 on the device. JSON, partial dates, ISO timestamps and UUIDs are all
 * text; the distinction drives the validators and the row types, not the DDL.
 */
export type PortableType =
  | 'text'
  | 'integer'
  | 'bigint'
  | 'real'
  | 'boolean'
  | 'json'
  | 'date-partial'
  | 'timestamp-iso'
  | 'uuid'

/** Only on server-only tables: a monotonic 64-bit sequence (`ops.server_seq`). */
export type ServerOnlyType = 'bigserial'

export type ColumnType = PortableType | ServerOnlyType

export interface ColumnDef {
  readonly type: ColumnType
  /** Defaults to NOT NULL. */
  readonly nullable?: true
  readonly primaryKey?: true
  readonly default?: string | number | boolean
  /** Allowed codes for a text column; validated by Zod, not by the database. */
  readonly enum?: readonly [string, ...string[]]
}

export type Owner = 'paroki' | 'keuskupan' | 'server' | 'device'

export interface IndexDef {
  readonly columns: readonly [string, ...string[]]
  readonly unique?: true
}

export interface TableDef {
  readonly name: string
  readonly columns: Readonly<Record<string, ColumnDef>>
  /** Synced tables exist in both dialects and carry the common sync columns (8.1). */
  readonly synced: boolean
  readonly owner: Owner
  /** Carries `lingkungan_id` for visibility and authorization by lingkungan (8.1). */
  readonly lingkunganScoped?: true
  /** A register book: carries `catatan_pinggir` (8.4). */
  readonly register?: true
  /**
   * Sensitive (release 19, owner 2026-10-08): only parish staff and admins receive and write it
   * (`TABEL_KHUSUS_STAF`, `PERAN_DATA_SENSITIF` in ../sync/visibility).
   */
  readonly khususStaf?: true
  readonly indexes?: readonly IndexDef[]
}

type Opts = {
  readonly nullable?: true
  readonly primaryKey?: true
  readonly default?: string | number | boolean
}

function kind<T extends ColumnType>(type: T) {
  return <const O extends Opts = Record<never, never>>(opts?: O): { readonly type: T } & O =>
    ({ type, ...((opts ?? {}) as O) }) as { readonly type: T } & O
}

const text = kind('text')
const integer = kind('integer')
const bigint = kind('bigint')
/** A decimal (double precision): a latitude or a longitude in degrees (schema 16). */
const real = kind('real')
const boolean = kind('boolean')
/** JSON stored as text; the column-specific Zod schema describes the parsed shape. */
const json = kind('json')
/** `YYYY`, `YYYY-MM` or `YYYY-MM-DD` as text; sorts lexicographically (8.1). */
const datePartial = kind('date-partial')
const timestamp = kind('timestamp-iso')
const uuid = kind('uuid')
const bigserial = kind('bigserial')

/** A text column restricted to a set of codes. */
function code<
  const E extends readonly [string, ...string[]],
  const O extends Opts = Record<never, never>,
>(values: E, opts?: O): { readonly type: 'text'; readonly enum: E } & O {
  return { type: 'text', enum: values, ...((opts ?? {}) as O) } as {
    readonly type: 'text'
    readonly enum: E
  } & O
}

function defineTable<const T extends TableDef>(table: T): T {
  return table
}

type ValueOf<C extends ColumnDef> = C extends { readonly enum: readonly (infer E extends string)[] }
  ? E
  : C['type'] extends 'integer' | 'bigint' | 'real' | 'bigserial'
    ? number
    : C['type'] extends 'boolean'
      ? boolean
      : string

/** The stored row shape of a table: enums as unions, JSON columns as their text form. */
export type RowOf<T extends TableDef> = {
  -readonly [K in keyof T['columns']]: T['columns'][K] extends { readonly nullable: true }
    ? ValueOf<T['columns'][K]> | null
    : ValueOf<T['columns'][K]>
}

// ---------------------------------------------------------------------------
// Enumerations (spec 8, labels in CONTENT.md section 6)
// ---------------------------------------------------------------------------

export const JENIS_UNIT = ['dekenat', 'paroki', 'stasi', 'lingkungan'] as const
export const STATUS_KELUARGA = ['aktif', 'pindah', 'bubar'] as const
export const JENIS_KELAMIN = ['L', 'P'] as const
export const STATUS_GEREJA = ['katolik', 'katekumen', 'non_katolik'] as const
export const STATUS_PERKAWINAN = [
  'belum_menikah',
  'menikah_gereja',
  'menikah_belum_gereja',
  'cerai',
  'janda_duda',
  'religius',
  'imam',
] as const
export const HUBUNGAN = ['kepala', 'istri', 'suami', 'anak', 'orang_tua', 'famili_lain'] as const
export const STATUS_HIDUP = ['hidup', 'meninggal'] as const
export const ALASAN_SELESAI = [
  'menikah',
  'pindah_keluarga',
  'pindah_paroki',
  'meninggal',
  'lainnya',
] as const
export const JENIS_BAPTIS = ['bayi', 'dewasa', 'bersyarat', 'diterima_penuh'] as const
export const BENTUK_PERKAWINAN = [
  'katolik_katolik',
  'katolik_katekumen',
  'campur_beda_gereja',
  'campur_beda_agama',
] as const
export const CARA_PERKAWINAN = ['biasa', 'sanatio', 'konvalidasi'] as const
export const STATUS_PERKAWINAN_ENTRI = ['sah', 'dinyatakan_batal', 'dispensasi_ratum'] as const
export const JENIS_DOKUMEN = [
  'ktp',
  'kk',
  'surat_baptis',
  'surat_komuni',
  'surat_krisma',
  'surat_perkawinan_gereja',
  'akta_nikah_sipil',
  'ijazah',
  'lainnya',
] as const
export const STATUS_UNGGAH = ['menunggu', 'terunggah', 'gagal'] as const
export const JENIS_SURAT = [
  'surat_baptis',
  'surat_komuni',
  'surat_krisma',
  'surat_perkawinan',
  'surat_kematian',
  'surat_pindah',
  'surat_pengantar_nikah',
  'kartu_keluarga',
] as const
export const ALASAN_TERBIT_ULANG = ['perkawinan', 'hilang', 'koreksi'] as const
export const TABEL_REGISTER = [
  'baptis',
  'komuni_pertama',
  'krisma',
  'perkawinan',
  'kematian',
] as const
export const JENIS_MUTASI = ['keluarga', 'umat'] as const
export const STATUS_MUTASI = ['diajukan', 'diterima', 'ditolak', 'dibatalkan'] as const
/**
 * The account roles. `pastor_paroki` (release 32, owner 2026-11-02): a Pastor Kepala Paroki or a
 * Pastor Rekan, who reads the whole parish, its staff-only fields included, and issues letters;
 * with `dekenat_id` set, also Ketua Wilayah over the parishes of his parish's wilayah. An app on
 * schema 17 stores a `pengguna` row with it as any other (no device check of the code) and names
 * it by its fallback, so the role came without a schema change.
 */
export const PERAN = [
  'admin_keuskupan',
  'uskup',
  'dekan',
  'pastor_paroki',
  'admin_paroki',
  'staf_paroki',
  'ketua_lingkungan',
  'ketua_stasi',
] as const
export const KEPADATAN = ['rapat', 'biasa'] as const
export const UKURAN_TEKS = ['kecil', 'sedang', 'besar'] as const
/** "Kurangi gerakan" (plan 9b): follow the OS, or reduced whatever the OS says. */
export const GERAKAN = ['system', 'reduced'] as const
export const STATUS_DOKUMEN_LOKAL = ['outbox', 'cache', 'tidak_ada'] as const
/** `situs`: a call of the parish website's feed (umpan situs, 2026-10-03), for its rate limit. */
export const JENIS_VERIFIKASI = ['cari', 'kode', 'situs'] as const
/**
 * The bounds per account on learning whether a NIK is held (release 14): POST /sync/ganda's
 * questions and NIKs asked, and the push's `duplicate_nik` refusals (`tolak_nik`, review I-1).
 * Release 20 counts the map's two questions to the server in the same table: an address looked
 * up (`cari_lokasi`) and a short map link opened (`buka_tautan`).
 */
export const BATAS_GANDA = ['permintaan', 'nik', 'tolak_nik', 'cari_lokasi', 'buka_tautan'] as const
/**
 * Where a family's location came from (release 20, schema 16): the address lookup, the device's
 * GPS during a visit, a pasted share-location link, or a pin a person placed on the map.
 */
export const LOKASI_SUMBER = ['alamat', 'gps', 'tautan', 'manual'] as const
/** Where a lingkungan's or a stasi's centre point came from: its name looked up, or a person. */
export const LOKASI_SUMBER_UNIT = ['alamat', 'manual'] as const
/**
 * How exact a family's location is: `tepat` for a person's own (GPS, a link, the map), else the
 * level the address lookup reached. Below `jalan` it is shown as an estimate ("perkiraan").
 */
export const LOKASI_PRESISI = ['tepat', 'jalan', 'kampung', 'kecamatan'] as const
/** The levels the address lookup reaches (`geokode_cache.presisi`). */
export const PRESISI_ALAMAT = ['jalan', 'kampung', 'kecamatan'] as const
/** Who someone is in Pelayan & pengurus (spec 2026-09-30, 4.1). */
export const JENIS_PELAYAN = [
  'imam',
  'diakon',
  'frater',
  'bruder',
  'suster',
  'katekis',
  'prodiakon',
  'awam',
  'lainnya',
] as const
/** The level of the place an assignment is at (spec 2026-09-30, 4.2; no wilayah, spec 2026-10-02). */
export const TINGKAT_PENUGASAN = ['keuskupan', 'dekenat', 'paroki', 'stasi', 'lingkungan'] as const
/** The kinds of a parish's groups (spec 2026-10-02 hierarki, 5.1): Seksi / komisi, Kelompok kategorial. */
export const JENIS_KELOMPOK = ['seksi', 'kategorial'] as const
/**
 * Every position by level (spec 2026-09-30, 4.2; spec 2026-10-02 hierarki, 5.2). One code per
 * position; a code names its level, and the five group positions (`kelompok_*`) also hold at a
 * stasi, the level of their group. `../pelayan.ts` carries each one's group, reserve, account
 * role and levels.
 */
export const KODE_JABATAN = [
  'uskup',
  'uskup_emeritus',
  'vikaris_jenderal',
  'vikaris_episkopal',
  'sekretaris_kanselir',
  'ekonom',
  'ketua_komisi',
  'lainnya_keuskupan',
  'dekan',
  'lainnya_dekenat',
  'pastor_kepala_paroki',
  'pastor_rekan',
  'pastor_pembantu',
  'diakon_paroki',
  'frater_top',
  'bruder_paroki',
  'suster_paroki',
  'katekis_paroki',
  'prodiakon_paroki',
  'koster',
  'staf_sekretariat',
  'lainnya_paroki',
  'dpp_ketua',
  'dpp_wakil_ketua',
  'dpp_sekretaris',
  'dpp_bendahara',
  'dpp_koordinator_bidang',
  'dpp_anggota',
  'dkp_ketua',
  'dkp_sekretaris',
  'dkp_bendahara',
  'dkp_anggota',
  'kelompok_ketua',
  'kelompok_wakil_ketua',
  'kelompok_sekretaris',
  'kelompok_bendahara',
  'kelompok_anggota',
  'pastor_melayani_stasi',
  'katekis_stasi',
  'prodiakon_stasi',
  'guru_agama',
  'lainnya_stasi',
  'stasi_ketua',
  'stasi_wakil_ketua',
  'stasi_sekretaris',
  'stasi_bendahara',
  'stasi_anggota',
  'lingkungan_ketua',
  'lingkungan_wakil_ketua',
  'lingkungan_sekretaris',
  'lingkungan_bendahara',
  'lingkungan_anggota',
] as const

/**
 * The parish data sheet's lists (release 19, owner 2026-10-08; labels in CONTENT.md). A person's
 * religion and previous religion: SATU stores the religion of a non-Catholic spouse too.
 */
export const AGAMA_UMAT = [
  'katolik',
  'protestan',
  'islam',
  'hindu',
  'buddha',
  'konghucu',
  'kepercayaan',
  'lainnya',
] as const
/** The school a person attends now; `pendidikan_kode` stays the highest level completed. */
export const PENDIDIKAN_SAAT_INI = [
  'belum_sekolah',
  'paud_tk',
  'sd',
  'smp',
  'sma_smk',
  'd1_d3',
  'd4_s1',
  's2',
  's3',
] as const
/** Sensitive (`umat_profil`): staff and admins only. */
export const GOLONGAN_DARAH = ['a', 'b', 'ab', 'o', 'tidak_tahu'] as const
/**
 * Sensitive (`umat_profil`): the monthly income in bands, never an amount. A typed amount reads
 * into its band (`../dataUmat` `bandPendapatan`): the lower edge belongs to the band, the upper one
 * to the next, except 6 juta, which is still "4–6 jt" ("> 6 jt" is more than 6 juta).
 */
export const PENDAPATAN_BULANAN = [
  'kurang_500rb',
  '500rb_1jt',
  '1jt_2jt',
  '2jt_3jt',
  '3jt_4jt',
  '4jt_6jt',
  'lebih_6jt',
] as const
/** Sensitive (`umat_profil`). */
export const ASURANSI_KESEHATAN = [
  'bpjs_kelas_1',
  'bpjs_kelas_2',
  'bpjs_kelas_3',
  'bpjs_pbi',
  'swasta',
  'tidak_ada',
] as const
/** A household's housing (`keluarga.status_rumah`). */
export const STATUS_RUMAH = [
  'milik_sendiri',
  'sewa_kontrak',
  'menumpang',
  'rumah_dinas',
  'lainnya',
] as const
/** The kind of a marriage received elsewhere (`referensi_sakramen_luar.perkawinan`). */
export const JENIS_PERKAWINAN_LUAR = ['katolik', 'campur_beda_gereja', 'beda_agama'] as const

export type Peran = (typeof PERAN)[number]

// ---------------------------------------------------------------------------
// Column groups shared by every synced table (8.1) and every register (8.4)
// ---------------------------------------------------------------------------

const SYNC_HEAD = {
  /** UUID v7 generated on the device that creates the row. */
  id: uuid({ primaryKey: true }),
  /** Owning parish; NULL means diocese-owned reference data. */
  paroki_id: uuid({ nullable: true }),
} as const

const SYNC_TAIL = {
  dibuat_pada: timestamp(),
  diubah_pada: timestamp(),
  /** Soft delete; NULL when live. */
  dihapus_pada: timestamp({ nullable: true }),
  /** JSON map field -> HLC of the last write to that field; drives the merge (9.3). */
  _hlc: json({ default: '{}' }),
} as const

/** Book reference and minister, identical on the five registers (8.4). */
const REGISTER_HEAD = {
  buku: text(),
  halaman: integer({ nullable: true }),
  nomor: integer(),
  tanggal: datePartial({ nullable: true }),
  tempat: text({ nullable: true }),
  /** Minister name as written in the book ("Peneguh" on perkawinan, "Pelayan" elsewhere). */
  pelayan: text({ nullable: true }),
  pelayan_id: uuid({ nullable: true }),
} as const

const REGISTER_TAIL = {
  catatan: text({ nullable: true }),
  /** JSON array of `{ tanggal, teks, oleh }`; append-only, never rewritten (8.4). */
  catatan_pinggir: json({ default: '[]' }),
  /**
   * JSON array of the required columns the copied book does not record ("Tidak tercatat di
   * buku", owner 2026-10-02): those columns stay empty and the entry counts as incomplete.
   * NULL (or absent, from an app before it) when every required column was filled.
   */
  tidak_tercatat: json({ nullable: true }),
} as const

/** Every synced table is filtered by owner on pull (9.4). */
const PAROKI_IDX: IndexDef = { columns: ['paroki_id'] }

// ---------------------------------------------------------------------------
// 8.2 Territory
// ---------------------------------------------------------------------------

export const unit_teritorial = defineTable({
  name: 'unit_teritorial',
  synced: true,
  // Dekenat and paroki rows are diocese-owned (paroki_id NULL); stasi and below carry
  // their parish's id. The tree is edited in Administrasi Keuskupan.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    jenis: code(JENIS_UNIT),
    /**
     * Parent unit; NULL for a dekenat (and a paroki outside any dekenat). A lingkungan hangs off
     * a paroki (the parish centre) or a stasi; there is no wilayah level (spec 2026-10-02 hierarki).
     */
    induk_id: uuid({ nullable: true }),
    nama: text(),
    /** Short code used in letter numbering at parish level. */
    kode: text({ nullable: true }),
    urutan: integer({ default: 0 }),
    /**
     * The centre point of a lingkungan or a stasi (schema 16, Peta umat): where a family without a
     * better location is shown, marked as an estimate. Looked up from the unit's name (`alamat`)
     * or set in Administrasi › Wilayah › Atur titik pusat (`manual`: the map, a link or GPS).
     * Degrees (WGS 84); both or neither. NULL for every other unit and until set.
     */
    lat: real({ nullable: true }),
    lng: real({ nullable: true }),
    lokasi_sumber: code(LOKASI_SUMBER_UNIT, { nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['induk_id'] }],
})

// ---------------------------------------------------------------------------
// 8.3 Families and people
// ---------------------------------------------------------------------------

export const keluarga = defineTable({
  name: 'keluarga',
  synced: true,
  owner: 'paroki',
  lingkunganScoped: true,
  columns: {
    ...SYNC_HEAD,
    /** Lingkungan lives on the family because a household has one address. */
    lingkungan_id: uuid(),
    /**
     * The parish number, unique per parish (the push refuses a second live holder): never typed
     * since release 18 (owner, 2026-10-08), generated for every new family (the parish's next
     * number, retired numbers skipped) and on a transfer in. Shown only while the family has no
     * government number, with *No. KK Disdukcapil belum tersedia* (`../nomorKk`); after that it
     * stays as history, searchable, never re-issued. Required, as apps on schema 13 expect.
     */
    nomor_kk_katolik: text(),
    /**
     * The government (Disdukcapil) KK number, 16 digits: the family's number everywhere a person
     * sees and does (release 18). Two families may share one (a married child still on the
     * parents' KK): a warning, never a refusal. Identifying, like a NIK: never in a URL, a log or
     * an error.
     */
    nomor_kk_pemerintah: text({ nullable: true }),
    /**
     * Kode keluarga (schema 14; owner, 2026-10-08): the parish's own family code ("AP-001", per
     * lingkungan), free text, trimmed, optional. Searchable, shown and downloaded; a code another
     * live family of the parish holds is a warning, never a refusal.
     */
    kode_keluarga: text({ nullable: true }),
    kepala_keluarga_id: uuid({ nullable: true }),
    alamat: text({ nullable: true }),
    rt_rw: text({ nullable: true }),
    /** Labelled "Kampung" in the UI (Kabupaten Asmat has distrik and kampung). */
    kampung_desa: text({ nullable: true }),
    /** Labelled "Distrik" in the UI. */
    kecamatan: text({ nullable: true }),
    /** From the Kartu Keluarga (Dokumen jadi teks, 2026-09-30). */
    kabupaten_kota: text({ nullable: true }),
    provinsi: text({ nullable: true }),
    /** Five digits when present (the validator checks). */
    kode_pos: text({ nullable: true }),
    /** The KK's issue date ("Dikeluarkan tanggal"). */
    tanggal_kk: datePartial({ nullable: true }),
    /** Household WhatsApp number. */
    telepon: text({ nullable: true }),
    /** Status rumah tinggal (schema 15): the household's housing; NULL while not recorded. */
    status_rumah: code(STATUS_RUMAH, { nullable: true }),
    /**
     * The home's location (schema 16, Peta umat; owner 2026-10-08), degrees (WGS 84), both or
     * neither: personal data, as an address is, so never in a URL, a log, an error, a print or a
     * download, never on /periksa; the backups keep it. The source and the precision say how it
     * was found (../peta/lokasi: an automatic result never replaces a better one); GPS keeps its
     * accuracy in metres. `lokasi_diubah_pada` stamps every change of these columns. All NULL
     * until a location is known: the map then shows the family at its unit's centre point.
     */
    lat: real({ nullable: true }),
    lng: real({ nullable: true }),
    lokasi_sumber: code(LOKASI_SUMBER, { nullable: true }),
    lokasi_presisi: code(LOKASI_PRESISI, { nullable: true }),
    lokasi_akurasi_m: integer({ nullable: true }),
    lokasi_diubah_pada: timestamp({ nullable: true }),
    /**
     * The address the location answers (review I1): a short hash of the four address parts the
     * lookup reads (../peta/alamat `kunciAlamat`), never the address text. "The address changed
     * since" compares it with the key of the address the family holds now, whatever order the
     * devices' edits arrive in.
     */
    lokasi_alamat_kunci: text({ nullable: true }),
    status: code(STATUS_KELUARGA, { default: 'aktif' }),
    /**
     * Pindah ke keuskupan lain (owner, 2026-10-08; schema 13): the family moved to a diocese
     * outside SATU, which has no receiving parish here, so no mutasi records it. Its status is
     * `pindah`; these say where to (the diocese and, when known, the parish, as typed) and
     * since when. Written and cleared together, by an admin paroki of the family's parish or
     * the admin keuskupan alone (`../pindahKeuskupan`), and on every living member at the
     * same time; NULL for every other family, a family moved inside SATU included.
     */
    pindah_keuskupan: text({ nullable: true }),
    pindah_paroki: text({ nullable: true }),
    pindah_tanggal: datePartial({ nullable: true }),
    /**
     * Desk-flow state, JSON `{ selesai, langkah: { kk, ktp, hp, email, baptis, komuni,
     * krisma, perkawinan, pendidikan, pekerjaan, anggota } }`, each step `selesai`,
     * `belum` or `tidak_ada`; written by every step save so a half-registered family is
     * real data (decided 2026-09-28).
     */
    pendaftaran: json({ nullable: true }),
    tanggal_mulai: datePartial({ nullable: true }),
    catatan: text({ nullable: true }),
    /**
     * Hapus keluarga (schema 17; release 31, owner 2026-11-02): why a family entered by mistake was
     * deleted ("data ganda", "salah ketik"), as typed, written with `dihapus_pada` by an admin
     * paroki of its parish or the admin keuskupan alone (`../hapusData`). NULL on every live family.
     */
    alasan_hapus: text({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['lingkungan_id'] },
    { columns: ['paroki_id', 'nomor_kk_katolik'] },
  ],
})

export const umat = defineTable({
  name: 'umat',
  synced: true,
  owner: 'paroki',
  lingkunganScoped: true,
  columns: {
    ...SYNC_HEAD,
    /** Denormalised from the current household; rewritten with `keluarga_id` (8.1). */
    lingkungan_id: uuid({ nullable: true }),
    nama_lengkap: text(),
    nama_baptis: text({ nullable: true }),
    nama_panggilan: text({ nullable: true }),
    jenis_kelamin: code(JENIS_KELAMIN, { nullable: true }),
    tempat_lahir: text({ nullable: true }),
    tanggal_lahir: datePartial({ nullable: true }),
    /** 16 digits when present. */
    nik: text({ nullable: true }),
    /** Nomor akta kelahiran as printed (Dokumen jadi teks, 2026-09-30). */
    nomor_akta_lahir: text({ nullable: true }),
    status_gereja: code(STATUS_GEREJA, { default: 'katolik' }),
    /**
     * The parish data sheet's fields (schema 15; owner, 2026-10-08), seen by whoever sees the
     * person, every one NULL while not recorded. `agama`: the person's religion (a non-Catholic
     * spouse's too); NULL reads as Katolik for a person whose `status_gereja` is katolik
     * (`../dataUmat` `agamaUmat`), so the rows from before need no backfill. `agama_sebelumnya`
     * and `gereja_asal`: where a person came from (the church or denomination as typed).
     */
    agama: code(AGAMA_UMAT, { nullable: true }),
    agama_sebelumnya: code(AGAMA_UMAT, { nullable: true }),
    gereja_asal: text({ nullable: true }),
    /** The person's place among the parents' children, from 1. */
    anak_ke: integer({ nullable: true }),
    status_perkawinan: code(STATUS_PERKAWINAN, { nullable: true }),
    /** The highest level completed (Dukcapil code). */
    pendidikan_kode: text({ nullable: true }),
    /** The school attended now, beside the highest level completed (schema 15). */
    pendidikan_saat_ini: code(PENDIDIKAN_SAAT_INI, { nullable: true }),
    jurusan: text({ nullable: true }),
    nama_sekolah: text({ nullable: true }),
    /** The year of graduation (or the one expected), four digits. */
    tahun_lulus: integer({ nullable: true }),
    /** The main occupation (Dukcapil code). */
    pekerjaan_kode: text({ nullable: true }),
    /** The profession as typed ("Guru Matematika", "Perawat"), beside the occupation. */
    profesi: text({ nullable: true }),
    /** A member of the credit union (the parish's CU "Ndar Sesepok"); NULL while not recorded. */
    anggota_cu: boolean({ nullable: true }),
    /** JSON array of free-text tags. */
    keterampilan: json({ nullable: true }),
    telepon: text({ nullable: true }),
    email: text({ nullable: true }),
    /** Current household and role in it, denormalised from anggota_keluarga. */
    keluarga_id: uuid({ nullable: true }),
    hubungan: code(HUBUNGAN, { nullable: true }),
    ayah_id: uuid({ nullable: true }),
    ibu_id: uuid({ nullable: true }),
    nama_ayah: text({ nullable: true }),
    nama_ibu: text({ nullable: true }),
    status_hidup: code(STATUS_HIDUP, { default: 'hidup' }),
    tanggal_meninggal: datePartial({ nullable: true }),
    /**
     * Pindah ke keuskupan lain (owner, 2026-10-08; schema 13): the person, alone or with their
     * family, moved to a diocese outside SATU. Set, the person is out of this parish's and the
     * diocese's active lists, counts and reports from that date; the record and its history stay.
     * The household link stays as it was, so "Batalkan pindah" restores the person whole.
     * Written and cleared as `keluarga`'s are; NULL while the person has not moved out.
     */
    pindah_keuskupan: text({ nullable: true }),
    pindah_paroki: text({ nullable: true }),
    pindah_tanggal: datePartial({ nullable: true }),
    /** Links to register entries in this parish. */
    baptis_id: uuid({ nullable: true }),
    komuni_id: uuid({ nullable: true }),
    krisma_id: uuid({ nullable: true }),
    /**
     * JSON for sacraments recorded at another parish: `{ baptis: { paroki, buku, halaman,
     * nomor, tanggal, tempat, pelayan, wali }, komuni, krisma, perkawinan: { …, saksi } }`
     * (`pelayan`, `wali`, `saksi` since Dokumen jadi teks, 2026-09-30; `nomor_surat`, the
     * certificate's number, and `gereja`, the church, on each, `jenis_baptis` on the baptism and
     * `jenis_perkawinan` on the marriage since release 19: JSON keys, no column).
     */
    referensi_sakramen_luar: json({ nullable: true }),
    /**
     * The person's own address (spec 2026-10-02 upacara bersama, Part B): shown and exported
     * only while the person has no family; a person in a family shows the family's. Labelled
     * as the family's columns are (Kampung, Distrik).
     */
    alamat: text({ nullable: true }),
    rt_rw: text({ nullable: true }),
    kampung_desa: text({ nullable: true }),
    kecamatan: text({ nullable: true }),
    kabupaten_kota: text({ nullable: true }),
    provinsi: text({ nullable: true }),
    catatan: text({ nullable: true }),
    /** Hapus umat (schema 17): why a person entered by mistake was deleted, as `keluarga`'s. */
    alasan_hapus: text({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['lingkungan_id'] },
    { columns: ['keluarga_id'] },
    { columns: ['baptis_id'] },
  ],
})

export const anggota_keluarga = defineTable({
  name: 'anggota_keluarga',
  synced: true,
  owner: 'paroki',
  lingkunganScoped: true,
  columns: {
    ...SYNC_HEAD,
    /** Denormalised from the household at the time of the row (8.1). */
    lingkungan_id: uuid({ nullable: true }),
    umat_id: uuid(),
    keluarga_id: uuid(),
    hubungan: code(HUBUNGAN),
    mulai: datePartial({ nullable: true }),
    /** NULL while the membership is current. */
    selesai: datePartial({ nullable: true }),
    alasan_selesai: code(ALASAN_SELESAI, { nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['lingkungan_id'] },
    { columns: ['umat_id'] },
    { columns: ['keluarga_id'] },
  ],
})

/**
 * A person's sensitive fields (schema 15; owner, 2026-10-08): blood type, ethnicity, income band
 * and health insurance. Only parish staff and admins (Admin and Staf Paroki, the admin keuskupan)
 * see and write them, so they live apart from `umat`, in a table the pull, the snapshot and the
 * catch-up never send to any other role and the push refuses from one (`TABEL_KHUSUS_STAF`,
 * `../sync/visibility`): a ketua's device never holds a value. One row per person, its `id` the
 * person's own `umat.id` (two devices recording a profile offline write one row, merged field by
 * field), owned by the person's parish; a transfer (mutasi) moves it with the person. Not
 * lingkungan-scoped: no ketua receives it, so nothing routes it by lingkungan.
 */
export const umat_profil = defineTable({
  name: 'umat_profil',
  synced: true,
  owner: 'paroki',
  khususStaf: true,
  columns: {
    ...SYNC_HEAD,
    golongan_darah: code(GOLONGAN_DARAH, { nullable: true }),
    /** As typed; the form suggests the values the parish already holds. */
    suku: text({ nullable: true }),
    pendapatan_bulanan: code(PENDAPATAN_BULANAN, { nullable: true }),
    asuransi_kesehatan: code(ASURANSI_KESEHATAN, { nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX],
})

// ---------------------------------------------------------------------------
// 8.4 Registers: book-faithful, owned by the parish whose book they are in
// ---------------------------------------------------------------------------

export const baptis = defineTable({
  name: 'baptis',
  synced: true,
  owner: 'paroki',
  register: true,
  columns: {
    ...SYNC_HEAD,
    ...REGISTER_HEAD,
    umat_id: uuid({ nullable: true }),
    nama: text(),
    nama_baptis: text({ nullable: true }),
    jenis_kelamin: code(JENIS_KELAMIN, { nullable: true }),
    tempat_lahir: text({ nullable: true }),
    tanggal_lahir: datePartial({ nullable: true }),
    nama_ayah: text({ nullable: true }),
    nama_ibu: text({ nullable: true }),
    agama_ayah: text({ nullable: true }),
    agama_ibu: text({ nullable: true }),
    wali_1: text({ nullable: true }),
    wali_2: text({ nullable: true }),
    /** `diterima_penuh` is reception into full communion. */
    jenis: code(JENIS_BAPTIS, { nullable: true }),
    /** As recorded in the book. */
    nomor_kk_katolik: text({ nullable: true }),
    /**
     * The person's later sacraments as the clerk typed them on the baptism entry, the margin
     * note of a baptism register (owner, 2026-10-08; schema 13): Komuni Pertama, Krisma and
     * Perkawinan, each a date (partial, like every SATU date) and a parish as written, the
     * marriage also the spouse's name. NULL while not typed. What SATU's own books record of
     * the same person is never copied here: it is read from the linked entries when the entry
     * is shown or printed (`../sakramenLanjut`), and a typed value differing from it stays,
     * the difference shown.
     */
    komuni_tanggal: datePartial({ nullable: true }),
    komuni_paroki: text({ nullable: true }),
    krisma_tanggal: datePartial({ nullable: true }),
    krisma_paroki: text({ nullable: true }),
    perkawinan_tanggal: datePartial({ nullable: true }),
    perkawinan_paroki: text({ nullable: true }),
    perkawinan_pasangan: text({ nullable: true }),
    ...REGISTER_TAIL,
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['umat_id'] }, { columns: ['paroki_id', 'buku', 'nomor'] }],
})

export const komuni_pertama = defineTable({
  name: 'komuni_pertama',
  synced: true,
  owner: 'paroki',
  register: true,
  columns: {
    ...SYNC_HEAD,
    ...REGISTER_HEAD,
    umat_id: uuid({ nullable: true }),
    nama: text(),
    tanggal_lahir: datePartial({ nullable: true }),
    /** Text: parish, book, number. */
    referensi_baptis: text({ nullable: true }),
    ...REGISTER_TAIL,
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['umat_id'] }, { columns: ['paroki_id', 'buku', 'nomor'] }],
})

export const krisma = defineTable({
  name: 'krisma',
  synced: true,
  owner: 'paroki',
  register: true,
  columns: {
    ...SYNC_HEAD,
    ...REGISTER_HEAD,
    umat_id: uuid({ nullable: true }),
    nama: text(),
    nama_krisma: text({ nullable: true }),
    tanggal_lahir: datePartial({ nullable: true }),
    wali_krisma: text({ nullable: true }),
    referensi_baptis: text({ nullable: true }),
    ...REGISTER_TAIL,
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['umat_id'] }, { columns: ['paroki_id', 'buku', 'nomor'] }],
})

export const perkawinan = defineTable({
  name: 'perkawinan',
  synced: true,
  owner: 'paroki',
  register: true,
  columns: {
    ...SYNC_HEAD,
    ...REGISTER_HEAD,
    pria_nama: text(),
    pria_tanggal_lahir: datePartial({ nullable: true }),
    pria_tempat_lahir: text({ nullable: true }),
    pria_agama: text({ nullable: true }),
    pria_nama_ayah: text({ nullable: true }),
    pria_nama_ibu: text({ nullable: true }),
    pria_referensi_baptis: text({ nullable: true }),
    pria_umat_id: uuid({ nullable: true }),
    wanita_nama: text(),
    wanita_tanggal_lahir: datePartial({ nullable: true }),
    wanita_tempat_lahir: text({ nullable: true }),
    wanita_agama: text({ nullable: true }),
    wanita_nama_ayah: text({ nullable: true }),
    wanita_nama_ibu: text({ nullable: true }),
    wanita_referensi_baptis: text({ nullable: true }),
    wanita_umat_id: uuid({ nullable: true }),
    saksi_1: text({ nullable: true }),
    saksi_2: text({ nullable: true }),
    /** The four discriminators the annual statistics questionnaire asks for. */
    bentuk: code(BENTUK_PERKAWINAN, { nullable: true }),
    cara: code(CARA_PERKAWINAN, { nullable: true }),
    dispensasi: text({ nullable: true }),
    status: code(STATUS_PERKAWINAN_ENTRI, { default: 'sah' }),
    ...REGISTER_TAIL,
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['pria_umat_id'] },
    { columns: ['wanita_umat_id'] },
    { columns: ['paroki_id', 'buku', 'nomor'] },
  ],
})

export const kematian = defineTable({
  name: 'kematian',
  synced: true,
  owner: 'paroki',
  register: true,
  columns: {
    ...SYNC_HEAD,
    ...REGISTER_HEAD,
    umat_id: uuid({ nullable: true }),
    nama: text(),
    tanggal_lahir: datePartial({ nullable: true }),
    tanggal_meninggal: datePartial({ nullable: true }),
    tempat_meninggal: text({ nullable: true }),
    tanggal_pemakaman: datePartial({ nullable: true }),
    tempat_pemakaman: text({ nullable: true }),
    referensi_baptis: text({ nullable: true }),
    ...REGISTER_TAIL,
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['umat_id'] }, { columns: ['paroki_id', 'buku', 'nomor'] }],
})

// ---------------------------------------------------------------------------
// 8.5 Documents
// ---------------------------------------------------------------------------

export const dokumen = defineTable({
  name: 'dokumen',
  synced: true,
  owner: 'paroki',
  lingkunganScoped: true,
  columns: {
    ...SYNC_HEAD,
    lingkungan_id: uuid({ nullable: true }),
    /** Exactly one of umat_id and keluarga_id is set (validated by Zod). */
    umat_id: uuid({ nullable: true }),
    keluarga_id: uuid({ nullable: true }),
    jenis: code(JENIS_DOKUMEN),
    /** Display title, e.g. "Ijazah SMA 2015". */
    judul: text(),
    nama_file: text(),
    mime: text(),
    /** Bytes after compression. */
    ukuran: integer(),
    hash_sha256: text(),
    /**
     * Object key `dokumen/<paroki_id>/<dokumen_id>.<ext>` in the `dokumen` bucket; historical,
     * never rewritten. The column keeps its R2-era name (Supabase Storage since 2026-09-29).
     */
    kunci_r2: text(),
    status_unggah: code(STATUS_UNGGAH, { default: 'menunggu' }),
    diunggah_oleh: uuid({ nullable: true }),
    diunggah_pada: timestamp({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['lingkungan_id'] },
    { columns: ['umat_id'] },
    { columns: ['keluarga_id'] },
  ],
})

// ---------------------------------------------------------------------------
// 8.6 Issued letters
// ---------------------------------------------------------------------------

export const surat = defineTable({
  name: 'surat',
  synced: true,
  owner: 'paroki',
  columns: {
    ...SYNC_HEAD,
    jenis: code(JENIS_SURAT),
    /** As printed. */
    nomor_surat: text(),
    /** Parsed sequence for the "next number" suggestion (12.2). */
    tahun: integer(),
    urutan: integer({ nullable: true }),
    tanggal: datePartial(),
    umat_id: uuid({ nullable: true }),
    keluarga_id: uuid({ nullable: true }),
    entri_id: uuid({ nullable: true }),
    entri_tabel: code(TABEL_REGISTER, { nullable: true }),
    /** Name and title printed. */
    ditandatangani_oleh: text(),
    /** 8 Crockford base32 characters from the first 40 bits of `id` (12.3). */
    kode_verifikasi: text(),
    dicetak_oleh: uuid({ nullable: true }),
    /** Set on a reissue; reprinting the same number writes nothing (decided 2026-09-28). */
    alasan_terbit_ulang: code(ALASAN_TERBIT_ULANG, { nullable: true }),
    menggantikan_surat_id: uuid({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [
    PAROKI_IDX,
    { columns: ['umat_id'] },
    { columns: ['keluarga_id'] },
    { columns: ['kode_verifikasi'] },
    { columns: ['paroki_id', 'tahun', 'jenis'] },
  ],
})

// ---------------------------------------------------------------------------
// 8.7 Mutasi
// ---------------------------------------------------------------------------

export const mutasi = defineTable({
  name: 'mutasi',
  synced: true,
  // Owned by the source parish; the destination writes only the decision fields (9.5).
  owner: 'paroki',
  columns: {
    ...SYNC_HEAD,
    jenis: code(JENIS_MUTASI),
    keluarga_id: uuid({ nullable: true }),
    umat_id: uuid({ nullable: true }),
    paroki_asal_id: uuid(),
    paroki_tujuan_id: uuid(),
    tanggal_permohonan: datePartial(),
    alasan: text({ nullable: true }),
    status: code(STATUS_MUTASI, { default: 'diajukan' }),
    tanggal_keputusan: datePartial({ nullable: true }),
    diputuskan_oleh: uuid({ nullable: true }),
    catatan_keputusan: text({ nullable: true }),
    /**
     * The destination's lingkungan (under its parish centre or one of its stasi) the family or
     * person joins: chosen by the destination's admin paroki in Terima, which refuses without it
     * (owner, 2026-10-04); the move writes it on every moved row. A decision column; NULL until
     * accepted, and on requests decided before schema 12.
     */
    lingkungan_tujuan_id: uuid({ nullable: true }),
    /**
     * The family's Catholic KK number in the source parish, trimmed, which acceptance retires
     * (owner, 2026-10-04): the server writes it when it moves the family, in the transaction that
     * gives the family the receiving parish's next number, and empty text when the family had
     * none. The source's next number counts it, so it is never issued there again, and the
     * source's prints of the moved family show it. Nothing else writes it: the push refuses it
     * from every device op. NULL on an open, refused or withdrawn request, a person's, and a
     * family's accepted before schema 12.
     */
    nomor_kk_asal: text({ nullable: true }),
    /**
     * JSON `{ nama, nomorKk, lingkungan, stasi, jumlahAnggota, jumlahDokumen }` written by
     * the source at filing (section 12): the destination decides before it holds any row of
     * the family, so the request itself must name what it is about.
     */
    ringkasan: json({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['paroki_tujuan_id'] }, { columns: ['keluarga_id'] }],
})

// ---------------------------------------------------------------------------
// Pelayan & pengurus (spec 2026-09-30, 5): the directory of everyone who serves
// ---------------------------------------------------------------------------

export const pelayan = defineTable({
  name: 'pelayan',
  synced: true,
  // Owned like `pengguna`: paroki_id NULL for the diocese's rows (clergy, religious, diocese
  // positions), set for the lay people a parish added. The admin keuskupan writes every
  // row; the push rules let an admin paroki write the lay rows of the own parish.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    /** As written on documents. */
    nama: text(),
    gelar: text({ nullable: true }),
    jenis: code(JENIS_PELAYAN),
    /** A priest's order ("diosesan" or e.g. "OSC"), a sister's congregation; free text. */
    ordo_kongregasi: text({ nullable: true }),
    /** Optional link to the person's own umat record. */
    umat_id: uuid({ nullable: true }),
    telepon: text({ nullable: true }),
    /** False for someone deceased or who left the diocese. */
    aktif: boolean({ default: true }),
    /**
     * Pindah ke keuskupan lain (owner, 2026-10-08; schema 13): the diocese the person left for,
     * as typed, and the date every current assignment ended; `aktif` is false with them. Written
     * and cleared by the admin keuskupan alone (`../pindahKeuskupan`); NULL otherwise.
     */
    pindah_keuskupan: text({ nullable: true }),
    pindah_tanggal: datePartial({ nullable: true }),
    catatan: text({ nullable: true }),
    /**
     * The photo of a priest or religious (owner, 2026-10-03): a `data:image/jpeg;base64,…` or
     * `data:image/png;base64,…` URL (`fotoPelayanSchema`), NULL for none and always NULL for a
     * lay person (`JENIS_BERFOTO`). Shown on screens only, never printed.
     */
    foto: text({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['umat_id'] }],
})

export const penugasan = defineTable({
  name: 'penugasan',
  synced: true,
  // paroki_id: the parish of the place; NULL for keuskupan and dekenat positions.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    pelayan_id: uuid(),
    /** The place (`unit_teritorial`); NULL for a keuskupan position. */
    unit_id: uuid({ nullable: true }),
    tingkat: code(TINGKAT_PENUGASAN),
    jabatan: code(KODE_JABATAN),
    /** The name for "Lainnya", the komisi or the bidang. */
    jabatan_lain: text({ nullable: true }),
    /**
     * The group (`kelompok`) a group position serves in (spec 2026-10-02 hierarki, 5.2): set for
     * the `kelompok_*` positions, NULL for every other one (`periksaPenugasan`).
     */
    kelompok_id: uuid({ nullable: true }),
    /** NULL when unknown. */
    sejak: datePartial({ nullable: true }),
    /** NULL while current. */
    sampai: datePartial({ nullable: true }),
    /** The SATU account held through this assignment; written by the server only. */
    pengguna_id: uuid({ nullable: true }),
    /**
     * The person agreed to appear on the parish website in this position (spec 2026-10-03 umpan
     * situs, 1): off for every row until someone switches it on, clergy included, and only on a
     * position the website's feed carries (`JABATAN_SITUS`). Switched in Pelayan & pengurus by
     * whoever may edit the assignment, as one column-only update with the two columns below.
     */
    tampil_di_situs: boolean({ default: false }),
    /** When `tampil_di_situs` was last switched; NULL while never. */
    tampil_di_situs_pada: timestamp({ nullable: true }),
    /**
     * The account that last switched it (the person's own, `asli`); no screen, address or log
     * shows it, and the feed never sends it.
     */
    tampil_di_situs_oleh: uuid({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['pelayan_id'] }, { columns: ['unit_id'] }],
})

// ---------------------------------------------------------------------------
// The parish's groups (spec 2026-10-02 hierarki, 5.1 and 6)
// ---------------------------------------------------------------------------

export const kelompok = defineTable({
  name: 'kelompok',
  synced: true,
  // A parish's seksi / komisi and kelompok kategorial. Every row names its parish (paroki_id,
  // never NULL: the push rule refuses one without). Owned like `penugasan`, so the admin
  // keuskupan writes any parish's (Paroki baru, Tambah and Ubah under each place on Pelayan &
  // pengurus) and the push rules give an admin paroki its own parish's rows; staff, ketua, dekan
  // and uskup read them.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    jenis: code(JENIS_KELOMPOK),
    /** "Seksi Liturgi", "OMK Stasi St. Yosef". */
    nama: text(),
    /** The place: the parish itself or one of its stasi. */
    unit_id: uuid(),
    /** False once retired ("Tidak aktif"): kept for history, offered for no new assignment. */
    aktif: boolean({ default: true }),
    urutan: integer({ default: 0 }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['unit_id'] }],
})

// ---------------------------------------------------------------------------
// 8.8 Reference data (diocese-owned, paroki_id NULL unless noted)
// ---------------------------------------------------------------------------

const kodeColumns = {
  ...SYNC_HEAD,
  kode: text(),
  label: text(),
  urutan: integer({ default: 0 }),
  /** Not on the government list; the UI marks these with a dagger. */
  tambahan_gereja: boolean({ default: false }),
  ...SYNC_TAIL,
} as const

export const kode_pendidikan = defineTable({
  name: 'kode_pendidikan',
  synced: true,
  owner: 'keuskupan',
  columns: kodeColumns,
  indexes: [{ columns: ['kode'] }],
})

export const kode_pekerjaan = defineTable({
  name: 'kode_pekerjaan',
  synced: true,
  owner: 'keuskupan',
  columns: kodeColumns,
  indexes: [{ columns: ['kode'] }],
})

export const pengguna = defineTable({
  name: 'pengguna',
  synced: true,
  // paroki_id is the user's parish (NULL for diocese roles), which is also who manages
  // the account. `id` equals the Supabase auth user id by convention only: no foreign key
  // to the auth schema.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    nama: text(),
    peran: code(PERAN),
    dekenat_id: uuid({ nullable: true }),
    /** Set only for ketua_lingkungan; not a lingkungan-scoped table in the 8.1 sense. */
    lingkungan_id: uuid({ nullable: true }),
    /** Set only for ketua_stasi: the stasi whose lingkungan the account acts in. */
    stasi_id: uuid({ nullable: true }),
    aktif: boolean({ default: true }),
    /**
     * Hapus akun (schema 17; release 31): why an account already switched off was deleted, as the
     * admin typed it. Written by /api/admin with `dihapus_pada`, once its sign-in is gone from
     * Supabase Auth; the row stays, so every change it made still names it. NULL otherwise.
     */
    alasan_hapus: text({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX],
})

export const perangkat = defineTable({
  name: 'perangkat',
  synced: true,
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    /** Who enrolled it. */
    pengguna_id: uuid(),
    nama: text(),
    platform: text({ nullable: true }),
    versi_aplikasi: text({ nullable: true }),
    /** Last server_seq the device has pulled. */
    kursor: bigint({ default: 0 }),
    terakhir_sinkron: timestamp({ nullable: true }),
    dicabut_pada: timestamp({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX, { columns: ['pengguna_id'] }],
})

export const statistik_manual = defineTable({
  name: 'statistik_manual',
  synced: true,
  // paroki_id NULL for diocese-level figures, set for a parish's own figures.
  owner: 'keuskupan',
  columns: {
    ...SYNC_HEAD,
    tahun: integer(),
    /** Question key of the annual statistics form, e.g. `imam_diosesan`, `suster`. */
    kunci: text(),
    nilai: integer({ nullable: true }),
    catatan: text({ nullable: true }),
    ...SYNC_TAIL,
  },
  indexes: [{ columns: ['paroki_id', 'tahun'] }],
})

// ---------------------------------------------------------------------------
// 8.9 Parish settings: one row per parish
// ---------------------------------------------------------------------------

export const pengaturan_paroki = defineTable({
  name: 'pengaturan_paroki',
  synced: true,
  owner: 'paroki',
  columns: {
    ...SYNC_HEAD,
    /** Short parish name for phone headers, e.g. "Katedral Agats". */
    nama_pendek: text({ nullable: true }),
    /**
     * Letter numbering pattern (12.2), JSON `{ segmen: [...], pemisah, urutanBersama,
     * kodeJenis: { surat_baptis: 'SB', ... } }`.
     */
    pola_nomor_surat: json({ nullable: true }),
    penanda_tangan_nama: text({ nullable: true }),
    penanda_tangan_jabatan: text({ nullable: true }),
    /** JSON array of letterhead lines. */
    kop_surat: json({ nullable: true }),
    /**
     * The parish's logo (spec 2026-10-03 logo paroki): a PNG or JPEG data URL the device
     * shrank before saving (`logoParokiSchema`), shown in place of the cathedral mark on the
     * kop and in the header; null keeps the cathedral mark. It syncs with the row, so it
     * prints offline.
     */
    logo: text({ nullable: true }),
    /**
     * The town the parish's letters are made in, printed before the date ("Agats, 3 Oktober
     * 2026"; `tempatSuratSchema`, owner 2026-10-03); null prints the diocese's town
     * (`suratStrings.penutup.tempatBawaan`). Never guessed from the parish's name.
     */
    tempat_surat: text({ nullable: true }),
    /** The parish's word for the base unit: "Lingkungan" by default, "Kombas" in some parishes. */
    sebutan_unit_dasar: text({ default: 'Lingkungan' }),
    /** JSON per register book, e.g. `{ baptis: 10, perkawinan: 5 }`; default 10 (entry mode). */
    baris_per_halaman: json({ nullable: true }),
    kepadatan_bawaan: code(KEPADATAN, { default: 'biasa' }),
    ukuran_teks_bawaan: code(UKURAN_TEKS, { default: 'sedang' }),
    ...SYNC_TAIL,
  },
  indexes: [PAROKI_IDX],
})

// ---------------------------------------------------------------------------
// 8.10 Sync tables, server side (Postgres only)
// ---------------------------------------------------------------------------

/** The operation log (9.2). Columns mirror the wire Op field for field. */
export const ops = defineTable({
  name: 'ops',
  synced: false,
  owner: 'server',
  columns: {
    op_id: uuid({ primaryKey: true }),
    /** Monotonic; pushes are serialised by an advisory lock so it equals commit order. */
    server_seq: bigserial(),
    /** "server" for system ops (9.8). */
    device_id: text(),
    user_id: uuid(),
    paroki_id: uuid({ nullable: true }),
    /** Mirrors the row's column on the four lingkungan-scoped tables; NULL elsewhere. */
    lingkungan_id: uuid({ nullable: true }),
    table: text(),
    row_id: uuid(),
    hlc: text(),
    schema_version: integer(),
    /** JSON of the changed columns only; a creation carries all columns. */
    fields: json(),
    received_at: timestamp(),
    /**
     * The row's `paroki_id` and `lingkungan_id` before this op (NULL when the op created
     * the row). Pull evaluates spec 9.4 with the previous row, so the parish or lingkungan
     * a row left still receives the op that moved it out, even after retention (9.9) has
     * deleted the row's earlier ops.
     */
    sebelum_paroki_id: uuid({ nullable: true }),
    sebelum_lingkungan_id: uuid({ nullable: true }),
  },
  indexes: [
    { columns: ['server_seq'], unique: true },
    { columns: ['paroki_id', 'server_seq'] },
    { columns: ['table', 'row_id'] },
  ],
})

/**
 * Watermark of the nightly export to the `cadangan` bucket (9.9, 17.4). The stream id `r2`
 * and the column `kunci_r2` keep their R2-era names (Supabase Storage since 2026-09-29).
 */
export const ekspor_ops = defineTable({
  name: 'ekspor_ops',
  synced: false,
  owner: 'server',
  columns: {
    /** One row, keyed by the export stream name (e.g. `r2`). */
    id: text({ primaryKey: true }),
    seq_terakhir: bigint({ default: 0 }),
    kunci_r2: text({ nullable: true }),
    diekspor_pada: timestamp({ nullable: true }),
  },
})

/** Who searched what, when (section 11); also feeds the per-user rate limit. */
export const verifikasi_log = defineTable({
  name: 'verifikasi_log',
  synced: false,
  owner: 'server',
  columns: {
    id: uuid({ primaryKey: true }),
    /** Null on a public code lookup: the holder of a letter needs no account (12.3). */
    pengguna_id: uuid({ nullable: true }),
    paroki_id: uuid({ nullable: true }),
    jenis: code(JENIS_VERIFIKASI),
    /** JSON of the query parameters. */
    parameter: json(),
    jumlah_hasil: integer({ default: 0 }),
    /** Truncated SHA-256 of the caller's address on a public lookup; carries its rate limit. */
    sidik_ip: text({ nullable: true }),
    dibuat_pada: timestamp(),
  },
  indexes: [{ columns: ['pengguna_id', 'dibuat_pada'] }, { columns: ['sidik_ip', 'dibuat_pada'] }],
})

/**
 * What a device reports about itself on every pull (section 19, spec 17.5): the size of
 * its outbox and its wall clock, so Status perangkat can show "Menunggu" and "Selisih
 * jam" per device without a wire change to the ops. Server only: written by the pull
 * route beside `perangkat.terakhir_sinkron`, read by GET /api/admin/perangkat.
 */
export const perangkat_status = defineTable({
  name: 'perangkat_status',
  synced: false,
  owner: 'server',
  columns: {
    perangkat_id: uuid({ primaryKey: true }),
    /** Ops still in the device's outbox after its push. */
    menunggu: integer({ nullable: true }),
    /** Device clock minus server clock at the report, in ms. */
    selisih_jam_ms: bigint({ nullable: true }),
    dilaporkan_pada: timestamp(),
  },
})

/**
 * Release 14 (owner, 2026-10-04): the per-account bounds on learning whether a NIK is held
 * anywhere in the diocese, POST /api/sync/ganda's and the push's `duplicate_nik` refusals (review
 * I-1). One row per account per bound (`permintaan`: questions, `nik`: NIKs asked, `tolak_nik`:
 * the push's NIK refusals), the start of its fixed window and the
 * count in it, counted and capped by one upsert; a later window replaces the
 * row, so an old window is gone on write. The key is "<pengguna_id>:<batas>". Server only; never
 * synced, never in a snapshot; never a NIK or a KK number. A server-only table leaves
 * SCHEMA_VERSION as it is.
 */
export const jatah_ganda = defineTable({
  name: 'jatah_ganda',
  synced: false,
  owner: 'server',
  columns: {
    id: text({ primaryKey: true }),
    pengguna_id: uuid(),
    batas: code(BATAS_GANDA),
    jendela: timestamp(),
    jumlah: integer({ default: 0 }),
  },
})

/**
 * Akun & keamanan (spec 2026-09-30, 5.3 and 7.3): the e-mail codes that recover a lost
 * second step. Server only, never synced and never in a snapshot: a row holds a salted
 * SHA-256 of the six digits, never the digits. A used, spent or superseded code is expired
 * in place (`kedaluwarsa_pada` set to that moment) so the hourly limit still counts it;
 * the nightly export purges rows that expired more than an hour ago. A server-only table
 * leaves SCHEMA_VERSION as it is (no op and no device changes).
 */
export const kode_pemulihan = defineTable({
  name: 'kode_pemulihan',
  synced: false,
  owner: 'server',
  columns: {
    id: uuid({ primaryKey: true }),
    pengguna_id: uuid(),
    /** `<salt, base64url>$<sha-256 hex of salt:pengguna_id:code>`. */
    kode_hash: text(),
    kedaluwarsa_pada: timestamp(),
    percobaan: integer({ default: 0 }),
    dibuat_pada: timestamp(),
  },
  indexes: [{ columns: ['pengguna_id', 'dibuat_pada'] }],
})

/**
 * Akun & keamanan (spec 2026-09-30, 4 and 7.1): one row per check of an account's current
 * password. The check is a real password grant against Supabase Auth from the API's own
 * address, so it is limited per account here (Supabase's own limit is per address, shared by
 * every account). Server only, never synced and never in a snapshot: only who and when, never
 * the password; an account's rows older than the window are deleted at its next check.
 */
export const percobaan_sandi = defineTable({
  name: 'percobaan_sandi',
  synced: false,
  owner: 'server',
  columns: {
    id: uuid({ primaryKey: true }),
    pengguna_id: uuid(),
    dibuat_pada: timestamp(),
  },
  indexes: [{ columns: ['pengguna_id', 'dibuat_pada'] }],
})

/**
 * Plan 9b (spec 2026-10-02 upacara-bersama-dan-akun, 5.4): a person's display preferences,
 * which follow the account to every device. Server only, never synced: read and written
 * through /api/akun/preferensi for the session's own account, one row per account, written
 * whole. Kept in the nightly snapshot (unlike the recovery codes): they are settings, not a
 * secret. A server-only table leaves SCHEMA_VERSION as it is (no op, no device change).
 */
export const preferensi_pengguna = defineTable({
  name: 'preferensi_pengguna',
  synced: false,
  owner: 'server',
  columns: {
    pengguna_id: uuid({ primaryKey: true }),
    kepadatan: code(KEPADATAN),
    ukuran_teks: code(UKURAN_TEKS),
    gerakan: code(GERAKAN),
    diubah_pada: timestamp(),
  },
})

/**
 * Peta umat (release 20): what the address lookup (POST /api/peta/cari-lokasi) learned from
 * Nominatim, so the same normalised address is never asked twice. The key is one step of the
 * lookup: the level and the normalised address parts (`kunciGeokode`), e.g.
 * "kampung|ewer, agats, asmat". A row holds the point and the level it was found at, or NULL for
 * "tidak ditemukan" (asked again after `GEOKODE_TIDAK_DITEMUKAN_MS`), the provider and when. Only
 * address parts ever reach it: never a name, a NIK or a KK number. Server only, never synced, out
 * of the snapshot (a cache is learned again). A server-only table leaves SCHEMA_VERSION as it is.
 */
export const geokode_cache = defineTable({
  name: 'geokode_cache',
  synced: false,
  owner: 'server',
  columns: {
    alamat: text({ primaryKey: true }),
    lat: real({ nullable: true }),
    lng: real({ nullable: true }),
    presisi: code(PRESISI_ALAMAT, { nullable: true }),
    penyedia: text(),
    dibuat_pada: timestamp(),
  },
})

/**
 * The one-request-per-second gate of Nominatim's usage policy across every function instance
 * (release 20): one row per provider, the earliest moment the next request may leave, in ms.
 * A request takes its turn under a transaction-level advisory lock (apps/api/src/peta/gerbang.ts).
 * Server only, never synced, out of the snapshot.
 */
export const geokode_gerbang = defineTable({
  name: 'geokode_gerbang',
  synced: false,
  owner: 'server',
  columns: {
    penyedia: text({ primaryKey: true }),
    giliran_ms: bigint(),
  },
})

// ---------------------------------------------------------------------------
// 8.10 Sync tables, device side (SQLite only, never synced)
// ---------------------------------------------------------------------------

/** Pending ops, pushed in batches (9.7). */
export const outbox = defineTable({
  name: 'outbox',
  synced: false,
  owner: 'device',
  columns: {
    op_id: uuid({ primaryKey: true }),
    /** The full Op as JSON. */
    op: json(),
    dibuat_pada: timestamp(),
    percobaan: integer({ default: 0 }),
    kesalahan_terakhir: text({ nullable: true }),
  },
})

/** Key-value state: cursor, last sync, server time offset, cached dashboard aggregates. */
export const sync_state = defineTable({
  name: 'sync_state',
  synced: false,
  owner: 'device',
  columns: {
    kunci: text({ primaryKey: true }),
    nilai: json(),
    diubah_pada: timestamp(),
  },
})

/** Local file state per document (10.2). */
export const dokumen_lokal = defineTable({
  name: 'dokumen_lokal',
  synced: false,
  owner: 'device',
  columns: {
    dokumen_id: uuid({ primaryKey: true }),
    status: code(STATUS_DOKUMEN_LOKAL),
    ukuran: integer({ nullable: true }),
    terakhir_dibuka: timestamp({ nullable: true }),
    percobaan_unggah: integer({ default: 0 }),
  },
})

/** Rejected ops with the server's reason, until dismissed (9.7). */
export const masalah_sinkron = defineTable({
  name: 'masalah_sinkron',
  synced: false,
  owner: 'device',
  columns: {
    id: uuid({ primaryKey: true }),
    op_id: uuid({ nullable: true }),
    tabel: text({ nullable: true }),
    row_id: uuid({ nullable: true }),
    /** Reason code returned by the server. */
    jenis: text(),
    pesan: text({ nullable: true }),
    /** The server's current row, JSON, when it sent one. */
    baris_server: json({ nullable: true }),
    /**
     * The op that was rejected (or could not be applied), JSON: its author, its HLC and
     * the fields it changed, so the problem card can say "perubahan 27 Sep 14.10 oleh
     * Maria K." after the outbox has let the op go (section 16). Null for problems that
     * have no op, such as a document that failed to upload.
     */
    op: json({ nullable: true }),
    dibuat_pada: timestamp(),
    ditutup_pada: timestamp({ nullable: true }),
  },
})

/** The identity the app trusts offline (15.2, 15.3). */
export const sesi_lokal = defineTable({
  name: 'sesi_lokal',
  synced: false,
  owner: 'device',
  columns: {
    pengguna_id: uuid({ primaryKey: true }),
    nama: text(),
    peran: code(PERAN),
    paroki_id: uuid({ nullable: true }),
    lingkungan_id: uuid({ nullable: true }),
    stasi_id: uuid({ nullable: true }),
    pin_hash: text(),
    pin_garam: text(),
    pin_gagal: integer({ default: 0 }),
    pin_terkunci_sampai: timestamp({ nullable: true }),
    terakhir_diverifikasi: timestamp(),
    terakhir_aktif: timestamp({ nullable: true }),
  },
})

// ---------------------------------------------------------------------------
// The contract as a whole
// ---------------------------------------------------------------------------

export const contract = {
  unit_teritorial,
  keluarga,
  umat,
  anggota_keluarga,
  umat_profil,
  baptis,
  komuni_pertama,
  krisma,
  perkawinan,
  kematian,
  dokumen,
  surat,
  mutasi,
  pelayan,
  penugasan,
  kelompok,
  kode_pendidikan,
  kode_pekerjaan,
  pengguna,
  perangkat,
  statistik_manual,
  pengaturan_paroki,
  ops,
  ekspor_ops,
  verifikasi_log,
  perangkat_status,
  jatah_ganda,
  kode_pemulihan,
  percobaan_sandi,
  preferensi_pengguna,
  geokode_cache,
  geokode_gerbang,
  outbox,
  sync_state,
  dokumen_lokal,
  masalah_sinkron,
  sesi_lokal,
} as const

export type Contract = typeof contract
export type TableName = keyof Contract
export type SyncedTableName = {
  [K in TableName]: Contract[K]['synced'] extends true ? K : never
}[TableName]

const allTables: readonly TableDef[] = Object.values(contract)

export const syncedTables: readonly TableDef[] = allTables.filter((t) => t.synced)
/** Tables that exist in Postgres: synced plus server-only. */
export const pgTables: readonly TableDef[] = allTables.filter((t) => t.owner !== 'device')
/** Tables that exist on the device: synced plus device-only. */
export const sqliteTables: readonly TableDef[] = allTables.filter((t) => t.owner !== 'server')

/** The four tables whose rows carry `lingkungan_id` for visibility (8.1, 9.4). */
export const LINGKUNGAN_SCOPED_TABLES = allTables
  .filter((t) => t.lingkunganScoped === true)
  .map((t) => t.name)

/**
 * The sensitive tables (`TableDef.khususStaf`; release 19: `umat_profil`): only parish staff and
 * admins receive or write them (`PERAN_DATA_SENSITIF`, ../sync/visibility).
 */
export const TABEL_KHUSUS_STAF: readonly SyncedTableName[] = allTables
  .filter((t) => t.khususStaf === true)
  .map((t) => t.name as SyncedTableName)

export function indexName(table: string, index: IndexDef): string {
  return `${table}_${index.columns.join('_')}_idx`
}

/**
 * The schema version that first synced each table newer than the release (spec 2026-10-02
 * hierarki, 6). An app parses every pulled op's table against its own list, so the pull route
 * sends a table's ops only to a client whose `?skema=` is at least this; a table not listed is
 * known to every version.
 */
export const TABEL_SEJAK: Readonly<Partial<Record<SyncedTableName, number>>> = {
  kelompok: 9,
  umat_profil: 15,
}

/**
 * The tables an older app still syncs that this server retired, by name only: `imam`, retired
 * from sync on 2026-10-04 (owner, step 1 of 2) and dropped from Postgres in release 21 (step 2,
 * "export and drop"; it held no row). No dialect has the table any more; the name stays so such
 * an app keeps working: the push refuses its op on one alone (`unknown_table`, the outbox lets
 * it go), the pull never sends one (the op log keeps the ones from before, append-only), the
 * snapshot route answers its request for one with an empty page instead of refusing the whole
 * snapshot, and a restore of an older backup that holds its rows or ops skips them.
 */
export const TABEL_PENSIUN: readonly string[] = ['imam']

/** Did this server retire `table` from sync (`TABEL_PENSIUN`)? */
export function tabelPensiun(table: string): boolean {
  return TABEL_PENSIUN.includes(table)
}

/**
 * The columns each schema version added to tables an older app already synced, from the first
 * release on (schema 5, 2026-09-30; spec 2026-10-02 hierarki, 6). An app before that version
 * applies a pulled op without such a column (its device's `writeRow` drops it) and keeps only
 * the stamp in `_hlc`, so the update fetches those tables once (apps/web/src/sync/loop.ts,
 * `tabelSusul`). 6: the family's KK fields and the person's birth certificate number (Dokumen
 * jadi teks); 7: a ketua stasi's stasi; 8: "Tidak tercatat di buku" on the five registers; 9:
 * an assignment's group; 10: a person's own address; 11: a parish's logo and the town its
 * letters are made in, the photo of a priest or religious, and an assignment's consent to the
 * parish website; 12: the lingkungan an accepted mutasi places its family or person in, and the
 * KK number an accepted family left behind in its source parish; 13: the later sacraments typed
 * on a baptism entry, and a family's, a person's and a Pelayan's move to another diocese; 14: a
 * family's Kode keluarga; 15: the parish data sheet's fields on a person and a family's housing
 * (the new table `umat_profil` is `TABEL_SEJAK`'s); 16: a family's home location and a unit's
 * centre point (Peta umat); 17: the reason a person, a family or an account was deleted. A
 * schema that adds a column to a synced table lists it here (apps/web/test/loop.test.ts reads the
 * device migrations).
 */
export const KOLOM_SEJAK: Readonly<
  Record<number, Readonly<Partial<Record<SyncedTableName, readonly string[]>>>>
> = {
  6: {
    keluarga: ['kabupaten_kota', 'provinsi', 'kode_pos', 'tanggal_kk'],
    umat: ['nomor_akta_lahir'],
  },
  7: { pengguna: ['stasi_id'] },
  8: {
    baptis: ['tidak_tercatat'],
    komuni_pertama: ['tidak_tercatat'],
    krisma: ['tidak_tercatat'],
    perkawinan: ['tidak_tercatat'],
    kematian: ['tidak_tercatat'],
  },
  9: { penugasan: ['kelompok_id'] },
  10: { umat: ['alamat', 'rt_rw', 'kampung_desa', 'kecamatan', 'kabupaten_kota', 'provinsi'] },
  11: {
    pengaturan_paroki: ['logo', 'tempat_surat'],
    pelayan: ['foto'],
    penugasan: ['tampil_di_situs', 'tampil_di_situs_pada', 'tampil_di_situs_oleh'],
  },
  12: { mutasi: ['lingkungan_tujuan_id', 'nomor_kk_asal'] },
  13: {
    baptis: [
      'komuni_tanggal',
      'komuni_paroki',
      'krisma_tanggal',
      'krisma_paroki',
      'perkawinan_tanggal',
      'perkawinan_paroki',
      'perkawinan_pasangan',
    ],
    keluarga: ['pindah_keuskupan', 'pindah_paroki', 'pindah_tanggal'],
    umat: ['pindah_keuskupan', 'pindah_paroki', 'pindah_tanggal'],
    pelayan: ['pindah_keuskupan', 'pindah_tanggal'],
  },
  14: { keluarga: ['kode_keluarga'] },
  15: {
    keluarga: ['status_rumah'],
    umat: [
      'agama',
      'agama_sebelumnya',
      'gereja_asal',
      'anak_ke',
      'pendidikan_saat_ini',
      'jurusan',
      'nama_sekolah',
      'tahun_lulus',
      'profesi',
      'anggota_cu',
    ],
  },
  16: {
    unit_teritorial: ['lat', 'lng', 'lokasi_sumber'],
    keluarga: [
      'lat',
      'lng',
      'lokasi_sumber',
      'lokasi_presisi',
      'lokasi_akurasi_m',
      'lokasi_diubah_pada',
      'lokasi_alamat_kunci',
    ],
  },
  17: { keluarga: ['alasan_hapus'], umat: ['alasan_hapus'], pengguna: ['alasan_hapus'] },
}

/** The schema of a pull that names none: the last version before the pull named its own. */
export const SKEMA_PULL_TANPA_VERSI = 8

/** Does a client on schema `skema` know `table`? */
export function tabelDikenal(table: string, skema: number): boolean {
  return (TABEL_SEJAK[table as SyncedTableName] ?? 0) <= skema
}
